import { inject, signal } from "@angular/core";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { getLocationFromCurrency } from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { SignalSubscriptionImpl } from "../../data-subscription";

export type SymbolData = {
  code: string; // Currency code (e.g., "AUD", "USD")
  amount: number; // Amount in the currency
  convertedAmount: number; // Amount converted to main currency
  country: string; // Country code (e.g., "AU", "US")
};

/**
 * This class only need to deal with angular dependency
 * and the core logic is in getAccountTableViews function which can be easily unit tested
 */
export class AccountSymbolTableSubscription extends SignalSubscriptionImpl<SymbolData[]> {
  private transactionStore = inject(TransactionStoreService);
  protected userStore = inject(UserStoreService);
  private accountId = signal<string>(null);

  get source() {
    const symbolData: SymbolData[] = [];
    const balances = this.transactionStore.balanceByAccountSymbols.balances();
    const baseCurrency = this.userStore.preferences.preferenceView().coreCurrency;
    const accountId = this.accountId();
    const AccountSymbolBalance = balances?.get(accountId);
    const generatedSymbols = this.transactionStore.generatedSymbols.collection();

    // Both guards matter independently: balances briefly holds stale, pre-clearStore() entries
    // for an account (clearStore() didn't used to clear it — now it does, but re-populating both
    // collections still isn't atomic), so generatedSymbols can be null here even when
    // AccountSymbolBalance is a real (if outdated) Map.
    if (!AccountSymbolBalance || !generatedSymbols) {
      return symbolData;
    }

    AccountSymbolBalance.forEach((value, symbol) => {
      // todo @alex change that when we have a proper symbols rates lookup function
      const view = generatedSymbols.find((cross) => cross.equals(symbol));

      const symData: SymbolData = {
        code: symbol,
        amount: value,
        convertedAmount: baseCurrency === symbol ? value : NaN,
        country: getLocationFromCurrency(symbol),
      };

      // todo @alex - replace that for a generic method of normalising for a day X
      if (view) {
        symData.convertedAmount = value * view.rate;
      }

      symbolData.push(symData);
    });

    return symbolData;
  }

  setAccountId(accountId: string) {
    if (this.accountId() !== accountId) {
      this.accountId.set(accountId);
    }
  }
}
